import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowDown, ArrowRight, ArrowUpRight, Check, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ButtonLink } from '@components/common/Button';
import { Container } from '@components/common/Container';
import { CareDiagram } from '@components/home/CareDiagram';
import { ProductShowcase } from '@components/home/ProductShowcase';
import { usePageTitle } from '@hooks/usePageTitle';
const problems = ['slow', 'setup', 'files', 'connection', 'guidance'];
const steps = ['listen', 'review', 'resolve'];
const carePoints = ['clarity', 'files', 'choice'];
const questions = ['remote', 'knowledge', 'price'];
export const HomePage = () => {
    const { t } = useTranslation();
    usePageTitle();
    return (_jsxs(Home, { children: [_jsxs(Hero, { "aria-labelledby": "home-title", children: [_jsxs(HeroInner, { children: [_jsxs(HeroCopy, { children: [_jsxs(Kicker, { children: [_jsx("i", { "aria-hidden": "true" }), t('home.hero.eyebrow')] }), _jsxs("h1", { id: "home-title", children: [_jsx("strong", { children: t('home.hero.titleLead') }), " ", t('home.hero.titleRest')] }), _jsx("p", { children: t('home.intro') }), _jsxs(HeroActions, { children: [_jsxs(ButtonLink, { to: "/contact", children: [t('actions.requestAssistance'), _jsx(ArrowUpRight, { size: 19, "aria-hidden": "true" })] }), _jsxs(TextAnchor, { href: "#como-funciona", children: [t('actions.howItWorks'), _jsx(ArrowDown, { size: 17, "aria-hidden": "true" })] })] }), _jsx(HeroNote, { children: t('home.hero.note') })] }), _jsx(CareDiagram, {})] }), _jsxs(HeroFoot, { children: [_jsx("span", { children: t('home.hero.footnote') }), _jsx("a", { href: "#ayuda", "aria-label": t('home.problems.title'), children: _jsx(ArrowDown, { size: 20, "aria-hidden": "true" }) })] })] }), _jsx(LightSection, { id: "ayuda", "aria-labelledby": "problems-title", children: _jsxs(EditorialGrid, { children: [_jsxs(SectionIntro, { children: [_jsxs(Kicker, { children: ["01 / ", t('home.problems.eyebrow')] }), _jsx(Title, { id: "problems-title", children: t('home.problems.title') }), _jsx(Body, { children: t('home.problems.intro') }), _jsxs(TextLink, { to: "/services", children: [t('home.problems.all'), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] })] }), _jsx(ProblemList, { children: problems.map((key, index) => (_jsxs(Problem, { children: [_jsxs(ProblemNumber, { "aria-hidden": "true", children: ["0", index + 1] }), _jsxs("div", { children: [_jsx("h3", { children: _jsxs(Link, { to: "/contact", children: [t(`home.problems.items.${key}.title`), _jsx(ArrowUpRight, { size: 20, "aria-hidden": "true" })] }) }), _jsx("p", { children: t(`home.problems.items.${key}.text`) })] })] }, key))) })] }) }), _jsx(ProcessSection, { id: "como-funciona", "aria-labelledby": "process-title", children: _jsxs(Container, { children: [_jsxs(ProcessHeading, { children: [_jsxs("div", { children: [_jsxs(Kicker, { children: ["02 / ", t('home.process.eyebrow')] }), _jsx(Title, { id: "process-title", children: t('home.process.title') })] }), _jsx(Body, { children: t('home.process.intro') })] }), _jsx(StepList, { children: steps.map((key, index) => (_jsxs(Step, { children: [_jsxs(StepMarker, { "aria-hidden": "true", children: ["0", index + 1, _jsx(ArrowRight, { size: 20 })] }), _jsx("h3", { children: t(`home.process.steps.${key}.title`) }), _jsx("p", { children: t(`home.process.steps.${key}.text`) })] }, key))) })] }) }), _jsx(RemoteSection, { "aria-labelledby": "remote-title", children: _jsxs(RemoteGrid, { children: [_jsxs(RemoteDrawing, { "aria-hidden": "true", children: [_jsxs("div", { className: "drawing-label", children: [t('home.remote.you'), _jsx("span", { children: t('home.remote.us') })] }), _jsxs("svg", { width: "480", height: "260", viewBox: "0 0 480 260", fill: "none", children: [_jsx("path", { d: "M28 116h240c43 0 64 20 64 58v18c0 25 18 42 42 42h60", stroke: "#A7E0DB", strokeWidth: "3" }), _jsx("path", { d: "m424 224 10 10-10 10", stroke: "#A7E0DB", strokeWidth: "3", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("rect", { x: "37", y: "43", width: "166", height: "114", rx: "5", fill: "#2A2F63", stroke: "#E3F6F5", strokeWidth: "2" }), _jsx("path", { d: "M24 167h192l12 11H12l12-11Z", fill: "#2A2F63", stroke: "#E3F6F5", strokeWidth: "2", strokeLinejoin: "round" }), _jsx("path", { d: "M90 91h60M90 105h35", stroke: "#A7E0DB", strokeWidth: "3", strokeLinecap: "round" }), _jsx("path", { d: "M374 118v-12a36 36 0 0 1 72 0v12M377 132h-5a5 5 0 0 1-5-5v-12a5 5 0 0 1 5-5h5v22Zm66 0h5a5 5 0 0 0 5-5v-12a5 5 0 0 0-5-5h-5v22ZM446 136v7c0 9-9 15-24 15", stroke: "#E3F6F5", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M416 158h-9", stroke: "#A7E0DB", strokeWidth: "5", strokeLinecap: "round" })] }), _jsx("p", { children: t('home.remote.caption') })] }), _jsxs("div", { children: [_jsx(Kicker, { children: t('home.remote.eyebrow') }), _jsx(Title, { id: "remote-title", children: t('home.remote.title') }), _jsx(Body, { children: t('home.remote.text') }), _jsx(RemoteNote, { children: t('home.remote.note') }), _jsxs(TextLink, { to: "/contact", children: [t('home.remote.link'), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] })] })] }) }), _jsx(LightSection, { "aria-labelledby": "care-title", children: _jsxs(EditorialGrid, { children: [_jsxs(SectionIntro, { children: [_jsxs(Kicker, { children: ["03 / ", t('home.care.eyebrow')] }), _jsx(Title, { id: "care-title", children: t('home.care.title') }), _jsx(Body, { children: t('home.care.intro') })] }), _jsx(CareList, { children: carePoints.map((key) => (_jsxs(CarePoint, { children: [_jsx(Check, { size: 21, "aria-hidden": "true" }), _jsxs("div", { children: [_jsx("h3", { children: t(`home.care.points.${key}.title`) }), _jsx("p", { children: t(`home.care.points.${key}.text`) })] })] }, key))) })] }) }), _jsx(FaqSection, { "aria-labelledby": "faq-title", children: _jsxs(EditorialGrid, { children: [_jsxs("div", { children: [_jsx(Kicker, { children: t('home.faq.eyebrow') }), _jsx(SmallTitle, { id: "faq-title", children: t('home.faq.title') })] }), _jsx(FaqList, { children: questions.map((key) => (_jsxs(Question, { children: [_jsxs("summary", { children: [t(`home.faq.items.${key}.question`), _jsx(Plus, { size: 19, "aria-hidden": "true" })] }), _jsx("p", { children: t(`home.faq.items.${key}.answer`) })] }, key))) })] }) }), _jsx(MoreSection, { "aria-labelledby": "more-title", children: _jsxs(Container, { children: [_jsx(Kicker, { id: "more-title", children: t('home.more') }), _jsxs(BusinessRow, { children: [_jsxs("div", { children: [_jsx(SmallTitle, { children: t('home.business.title') }), _jsx(Body, { children: t('home.business.text') })] }), _jsxs(TextLink, { to: "/services", children: [t('home.business.link'), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] })] })] }) }), _jsx(ProductShowcase, {}), _jsx(Closing, { "aria-labelledby": "closing-title", children: _jsxs(ClosingInner, { children: [_jsxs("div", { children: [_jsx(Kicker, { children: t('home.closing.eyebrow') }), _jsx(Title, { id: "closing-title", children: t('home.closing.title') }), _jsx(Body, { children: t('home.closing.text') })] }), _jsxs(ButtonLink, { to: "/contact", children: [t('actions.requestAssistance'), _jsx(ArrowUpRight, { size: 20, "aria-hidden": "true" })] })] }) })] }));
};
const Home = styled.div `
  h1,
  h2,
  h3,
  p {
    overflow-wrap: break-word;
  }
  h2,
  h3 {
    text-wrap: pretty;
  }
  h3 {
    margin: 0;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.4;
    letter-spacing: -0.025em;
  }
`;
const Kicker = styled.p `
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin: 0 0 1.5rem;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.085em;
  text-transform: uppercase;
  line-height: 1.6;
  i {
    width: 18px;
    height: 7px;
    flex: 0 0 auto;
    border-left: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
  }
`;
const Title = styled.h2 `
  margin: 0;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: clamp(2rem, 3.3vw, 3rem);
  font-weight: 400;
  line-height: 1.12;
  letter-spacing: -0.05em;
`;
const SmallTitle = styled(Title) `
  font-size: clamp(1.6rem, 2.4vw, 2.15rem);
  line-height: 1.2;
`;
const Body = styled.p `
  max-width: 38rem;
  margin: 1.5rem 0 0;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 1.0625rem;
  line-height: 1.75;
`;
const TextLink = styled(Link) `
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: 1rem;
  padding-block: 0.5rem;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.5;
  text-decoration: underline;
  text-underline-offset: 5px;
  text-decoration-color: transparent;
  transition: text-decoration-color 180ms ease;
  svg {
    flex: 0 0 auto;
    transition: transform 180ms ease;
  }
  &:hover {
    text-decoration-color: currentColor;
  }
  &:hover svg {
    transform: translate(2px, -2px);
  }
`;
const TextAnchor = styled.a `
  display: inline-flex;
  min-height: 48px;
  align-items: center;
  gap: 0.8rem;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.9375rem;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 5px;
  text-decoration-color: ${({ theme }) => theme.colors.line};
  &:hover {
    text-decoration-color: currentColor;
  }
  svg {
    flex: 0 0 auto;
  }
`;
const Hero = styled.section `
  background: ${({ theme }) => theme.colors.surface};
`;
const HeroInner = styled(Container) `
  display: grid;
  align-items: center;
  gap: 3.5rem;
  padding-block: clamp(3rem, 6vw, 6.5rem) 4rem;
  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1.28fr) minmax(0, 1fr);
    gap: clamp(2rem, 4vw, 5rem);
  }
  @media (min-width: 768px) and (max-width: 1023px) {
    > figure {
      max-width: 460px;
      justify-self: end;
      margin-top: -1rem;
    }
  }
  @media (max-width: 767px) {
    gap: 2.75rem;
    padding-bottom: 2rem;
    > figure {
      max-width: 380px;
      justify-self: center;
    }
  }
`;
const HeroCopy = styled.div `
  h1 {
    max-width: 720px;
    margin: 0;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: clamp(2.55rem, 4.8vw, 4.5rem);
    font-weight: 400;
    line-height: 1.055;
    letter-spacing: -0.065em;
  }
  h1 strong {
    display: block;
    font-weight: 700;
  }
  > p:not(${Kicker}) {
    max-width: 33rem;
    margin: 1.8rem 0 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1.125rem;
    line-height: 1.75;
  }
  @media (max-width: 767px) {
    h1 {
      font-size: clamp(2.5rem, 10.6vw, 3.6rem);
    }
    > p:not(${Kicker}) {
      font-size: 1rem;
    }
  }
  @media (max-width: 374px) {
    h1 {
      font-size: 2.25rem;
    }
  }
`;
const HeroActions = styled.div `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.5rem;
  margin-top: 2rem;
  > a:first-child {
    min-height: 54px;
  }
`;
const HeroNote = styled.div `
  margin-top: 1rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.muted};
`;
const HeroFoot = styled(Container) `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 72px;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.muted};
  a {
    display: grid;
    width: 44px;
    height: 44px;
    place-items: center;
    flex: 0 0 auto;
    color: ${({ theme }) => theme.colors.navy900};
  }
`;
const LightSection = styled.section `
  padding: clamp(4rem, 7.5vw, 7rem) 0;
  background: ${({ theme }) => theme.colors.white};
`;
const EditorialGrid = styled(Container) `
  display: grid;
  gap: 2.5rem;
  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: clamp(3rem, 8vw, 8rem);
  }
`;
const SectionIntro = styled.div `
  ${Title} {
    max-width: 460px;
  }
  ${TextLink} {
    margin-top: 1.5rem;
  }
`;
const ProblemList = styled.div `
  border-top: 1px solid ${({ theme }) => theme.colors.navy900};
`;
const Problem = styled.article `
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr);
  gap: 1rem;
  padding-block: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  h3 a {
    display: flex;
    min-height: 44px;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
  h3 a svg {
    flex: 0 0 auto;
    color: ${({ theme }) => theme.colors.blue600};
    transition: transform 180ms ease;
  }
  h3 a:hover {
    color: ${({ theme }) => theme.colors.blue600};
  }
  h3 a:hover svg {
    transform: translate(2px, -2px);
  }
  p {
    margin: 0.35rem 2rem 0 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1rem;
    line-height: 1.65;
  }
  @media (max-width: 767px) {
    grid-template-columns: 1.5rem minmax(0, 1fr);
    gap: 0.65rem;
    padding-block: 1.15rem;
    h3 {
      font-size: 1.125rem;
    }
    p {
      margin-right: 0;
    }
  }
`;
const ProblemNumber = styled.span `
  padding-top: 0.8rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.muted};
  font-variant-numeric: tabular-nums;
`;
const ProcessSection = styled(LightSection) `
  background: ${({ theme }) => theme.colors.surface};
`;
const ProcessHeading = styled.div `
  display: grid;
  gap: 1.25rem;
  margin-bottom: 3rem;
  @media (min-width: 900px) {
    grid-template-columns: 1.2fr 0.8fr;
    gap: 5rem;
    align-items: end;
    ${Body} {
      margin: 0;
    }
  }
`;
const StepList = styled.ol `
  display: grid;
  gap: 2rem;
  margin: 0;
  padding: 0;
  list-style: none;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2.5rem;
  }
`;
const Step = styled.li `
  p {
    margin: 0.8rem 0 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1rem;
    line-height: 1.7;
  }
`;
const StepMarker = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  margin-bottom: 1.5rem;
  border-top: 2px solid ${({ theme }) => theme.colors.mint200};
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.875rem;
  svg {
    color: ${({ theme }) => theme.colors.blue600};
  }
  @media (max-width: 767px) {
    margin-bottom: 0.75rem;
  }
`;
const RemoteSection = styled.section `
  padding-block: clamp(3.5rem, 6vw, 6rem);
  background: ${({ theme }) => theme.colors.navy900};
  color: white;
  ${Kicker} {
    color: ${({ theme }) => theme.colors.mint200};
  }
  ${Title} {
    color: white;
  }
  ${Body} {
    color: ${({ theme }) => theme.colors.mint50};
  }
  ${TextLink} {
    color: white;
    margin-top: 1.25rem;
  }
`;
const RemoteGrid = styled(Container) `
  display: grid;
  gap: 3rem;
  align-items: center;
  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
    gap: clamp(3rem, 7vw, 7rem);
  }
  @media (max-width: 899px) {
    > div:last-child {
      order: -1;
    }
  }
`;
const RemoteDrawing = styled.div `
  max-width: 480px;
  width: 100%;
  justify-self: center;
  .drawing-label {
    display: flex;
    justify-content: space-between;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(227, 246, 245, 0.3);
    font-size: 0.8125rem;
    color: ${({ theme }) => theme.colors.mint50};
  }
  svg {
    display: block;
    width: 100%;
    height: auto;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.mint200};
    font-size: 0.875rem;
  }
`;
const RemoteNote = styled.p `
  margin: 1rem 0 0;
  color: ${({ theme }) => theme.colors.mint200};
  font-size: 0.9375rem;
  line-height: 1.7;
`;
const CareList = styled.div `
  display: grid;
  align-content: start;
  gap: 2rem;
`;
const CarePoint = styled.article `
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 1.25rem;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  padding-top: 1.5rem;
  > svg {
    margin-top: 0.2rem;
    color: ${({ theme }) => theme.colors.blue600};
  }
  p {
    margin: 0.75rem 0 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1rem;
    line-height: 1.75;
  }
`;
const FaqSection = styled.section `
  padding-bottom: clamp(4rem, 7vw, 6rem);
`;
const FaqList = styled.div `
  border-top: 1px solid ${({ theme }) => theme.colors.line};
`;
const Question = styled.details `
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    min-height: 64px;
    padding-block: 1rem;
    color: ${({ theme }) => theme.colors.navy900};
    cursor: pointer;
    list-style: none;
    font-weight: 700;
    font-size: 1rem;
    line-height: 1.5;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary svg {
    flex-shrink: 0;
    transition: transform 180ms ease;
  }
  &[open] summary svg {
    transform: rotate(45deg);
  }
  p {
    max-width: 38rem;
    margin: 0 0 1.5rem;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1rem;
    line-height: 1.75;
  }
`;
const MoreSection = styled(LightSection) `
  background: ${({ theme }) => theme.colors.surface};
`;
const BusinessRow = styled.div `
  display: grid;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem 0 3rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  ${Body} {
    margin-top: 1rem;
    max-width: 44rem;
  }
  @media (min-width: 900px) {
    grid-template-columns: 1fr auto;
    gap: 4rem;
  }
`;
const Closing = styled.section `
  padding-block: clamp(3.5rem, 6vw, 6rem);
  background: ${({ theme }) => theme.colors.mint50};
`;
const ClosingInner = styled(Container) `
  display: grid;
  align-items: center;
  gap: 2rem;
  > a {
    justify-self: start;
    min-height: 56px;
  }
  ${Title} {
    max-width: 700px;
  }
  ${Body} {
    max-width: 40rem;
    margin-top: 1.25rem;
  }
  @media (min-width: 1024px) {
    grid-template-columns: 1fr auto;
    gap: 4rem;
  }
`;
